<script setup>
import { computed, reactive, watch, ref, useId } from 'vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import { toIntlLocale } from 'shared/helpers/intlLocale';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CompanySelector from 'dashboard/components-next/Companies/CompanySelector.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t, locale } = useI18n();
const formId = useId();
const fieldId = key => `${formId}-${key.toLowerCase()}`;
const { isWorkspaceV2 } = useWorkspaceV2();
const { currentAccount, isCloudFeatureEnabled } = useAccount();

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  companyId: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive(structuredClone(defaultState));
const initialState = ref(JSON.stringify(state));
const isDirty = computed(() => JSON.stringify(state) !== initialState.value);
const markSaved = () => {
  initialState.value = JSON.stringify(state);
};

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);
const hasCompaniesFeature = computed(
  () =>
    currentAccount.value?.id && isCloudFeatureEnabled(FEATURE_FLAGS.COMPANIES)
);
const showCompanySelector = computed(
  () =>
    hasCompaniesFeature.value &&
    (Boolean(state.companyId) || !state.additionalAttributes.companyName)
);

const emitContactUpdate = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  const { firstName, lastName, ...stateWithoutNames } = state;
  emit('update', stateWithoutNames);
};

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    companyId = '',
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  Object.assign(state, {
    id,
    name,
    companyId: companyId || '',
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
  });
};

const countryOptions = computed(() => {
  const names = new Intl.DisplayNames([toIntlLocale(locale?.value)], {
    type: 'region',
  });
  return countries.map(({ name, id }) => ({
    label: isWorkspaceV2.value ? names.of(id) || name : name,
    value: id,
  }));
});

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    label: t(`WORKSPACE_V2.CONTACT_FORM.FIELDS.${key}`),
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const formGroups = computed(() => {
  if (!isWorkspaceV2.value)
    return [{ key: 'legacy', fields: editDetailsForm.value }];
  return [
    {
      key: 'CONTACT',
      keys: ['FIRST_NAME', 'LAST_NAME', 'EMAIL_ADDRESS', 'PHONE_NUMBER'],
    },
    { key: 'LOCATION', keys: ['CITY', 'COUNTRY'] },
    { key: 'CONTEXT', keys: ['COMPANY_NAME', 'BIO'] },
  ].map(group => ({
    key: group.key,
    fields: group.keys.map(key =>
      editDetailsForm.value.find(field => field.key === key)
    ),
  }));
});

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    label: t(`WORKSPACE_V2.CONTACT_FORM.SOCIAL.${key}`),
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      await emitContactUpdate();
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const handleCompanySelection = async ({ id, name }) => {
  state.companyId = id || '';
  state.additionalAttributes.companyName = name || '';
  await emitContactUpdate();
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, structuredClone(defaultState));
  resetValidation();
  markSaved();
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) {
      prepareStateBasedOnProps();
      markSaved();
    }
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  isDirty,
  markSaved,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div
    class="flex w-full flex-col gap-6"
    :class="{
      'rounded-2xl border border-n-weak bg-n-solid-1 p-4 sm:p-5': isWorkspaceV2,
    }"
  >
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{
          isNewContact
            ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADD_CONTACT')
            : t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE')
        }}
      </span>
      <fieldset
        v-for="group in formGroups"
        :key="group.key"
        class="m-0 w-full min-w-0 border-0 p-0"
        :class="{ 'mt-3': isWorkspaceV2 }"
      >
        <legend
          v-if="isWorkspaceV2"
          class="mb-3 text-xs font-medium text-n-slate-11"
        >
          {{ t(`WORKSPACE_V2.CONTACT_FORM.GROUPS.${group.key}`) }}
        </legend>
        <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
          <div
            v-for="item in group.fields"
            :key="item.key"
            class="flex min-w-0 flex-col gap-1.5"
          >
            <label
              v-if="isWorkspaceV2"
              :id="`${fieldId(item.key)}-label`"
              :for="fieldId(item.key)"
              class="flex items-center gap-1.5 text-sm text-n-slate-12"
              ><span>{{ item.label }}</span></label
            >
            <ComboBox
              v-if="item.key === 'COUNTRY'"
              v-model="state.additionalAttributes.countryCode"
              :trigger-id="fieldId(item.key)"
              :trigger-labelledby="
                isWorkspaceV2 ? `${fieldId(item.key)}-label` : ''
              "
              :trigger-class="isWorkspaceV2 ? '!h-11 !min-h-11' : ''"
              :options="countryOptions"
              :placeholder="item.placeholder"
              :aria-label="isWorkspaceV2 ? item.label : item.placeholder"
              class="[&>div>button]:h-8"
              :class="{
                '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                  !isDetailsView,
                '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
              }"
              @update:model-value="handleCountrySelection"
            />
            <PhoneNumberInput
              v-else-if="item.key === 'PHONE_NUMBER'"
              v-model="getFormBinding(item.key).value"
              :input-id="fieldId(item.key)"
              :dial-code-label="t('WORKSPACE_V2.CONTACT_FORM.DIAL_CODE')"
              :input-labelledby="
                isWorkspaceV2 ? `${fieldId(item.key)}-label` : ''
              "
              :class="{
                '[&>div]:!h-11 [&_input]:!h-11 [&_button]:!h-11': isWorkspaceV2,
              }"
              :placeholder="item.placeholder"
              :aria-label="isWorkspaceV2 ? item.label : item.placeholder"
              :show-border="isDetailsView"
            />
            <CompanySelector
              v-else-if="item.key === 'COMPANY_NAME' && showCompanySelector"
              :trigger-id="fieldId(item.key)"
              :trigger-labelledby="
                isWorkspaceV2 ? `${fieldId(item.key)}-label` : ''
              "
              :trigger-class="isWorkspaceV2 ? '!h-11 !min-h-11' : ''"
              :model-value="state.companyId"
              :selected-name="state.additionalAttributes.companyName"
              :is-details-view="isDetailsView"
              @select="handleCompanySelection"
            />
            <Input
              v-else
              :id="fieldId(item.key)"
              v-model="getFormBinding(item.key).value"
              :placeholder="item.placeholder"
              :aria-label="isWorkspaceV2 ? item.label : item.placeholder"
              :message-type="getMessageType(item.key)"
              :custom-input-class="`${isWorkspaceV2 ? '!h-11 !min-h-11' : 'h-8 !pt-1 !pb-1'} ${
                !isDetailsView
                  ? '[&:not(.error,.focus)]:!outline-transparent'
                  : ''
              }`"
              class="w-full"
              @input="
                isValidationField(item.key) &&
                  v$[getValidationKey(item.key)].$touch()
              "
              @blur="
                isValidationField(item.key) &&
                  v$[getValidationKey(item.key)].$touch()
              "
            />
          </div>
        </div>
      </fieldset>
    </div>
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div
        :class="
          isWorkspaceV2
            ? 'grid w-full grid-cols-1 gap-3 sm:grid-cols-2'
            : 'flex flex-wrap gap-2'
        "
      >
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          :class="
            isWorkspaceV2
              ? 'flex min-w-0 flex-col gap-1.5'
              : `flex items-center h-8 gap-2 px-2 rounded-xl bg-n-alpha-2 ${isDetailsView ? 'dark:bg-n-solid-2' : 'dark:bg-n-solid-3'}`
          "
        >
          <label
            v-if="isWorkspaceV2"
            :for="fieldId(item.key)"
            class="flex items-center gap-1.5 text-sm text-n-slate-12"
            ><Icon :icon="item.icon" class="size-4 text-n-slate-11" /><span>{{
              item.label
            }}</span></label
          >
          <div
            class="flex min-w-0 items-center gap-2"
            :class="{
              'h-11 rounded-2xl bg-n-alpha-black2 px-3 focus-within:ring-2 focus-within:ring-n-brand':
                isWorkspaceV2,
            }"
          >
            <Icon
              v-if="!isWorkspaceV2"
              :icon="item.icon"
              class="flex-shrink-0 text-n-slate-11 size-4"
            />
            <input
              :id="fieldId(item.key)"
              v-model="
                state.additionalAttributes.socialProfiles[
                  item.key.toLowerCase()
                ]
              "
              :class="{ '!h-11 !min-w-0 !w-full': isWorkspaceV2 }"
              class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
              :placeholder="item.placeholder"
              :aria-label="isWorkspaceV2 ? item.label : item.placeholder"
              :size="isWorkspaceV2 ? undefined : item.placeholder.length"
              @input="emit('update', state)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
